import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { UPGRADE_SOURCE } from 'pricing-pages-lib/constants/QueryParams';
import { getRedirectQueryParamsForCheckout } from 'pricing-pages-ui-components/utils/queryParamUtils';
import { GlobalWebBrandingStyles } from 'pricing-pages-ui-components/stylesheets/GlobalWebBranding.styles';
import { SideNavOverrideStyles } from 'pricing-pages-ui-components/stylesheets/SideNavOverride.styles';
import { isAppAuthSelector, isBillingAdminSelector } from 'pricing-pages-ui/selectors/AuthSelectors';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import { getPrimaryTracker } from 'usage-tracker-container';
import { makeLoadable } from '../components/common/LoadableWrapper';
import { MISMATCHED_RESPONSES, MISMATCHED_SEATS_GATING } from 'pricing-pages-ui-components/constants/ErrorCauses';
import { useBulkPurchaseOptions } from '../hooks/useBulkPurchaseOptions';
import { useDisplayedCurrencyCode, usePricingPageCurrencies } from '../hooks/useCurrencies';
import { useMismatchedCohortingCheck } from '../hooks/useMismatchedCohortingCheck';
import ErrorPage from '../pages/ErrorPage';
import { useAppSelector } from '../reducers';
import { areGatesPopulatedSelector } from '../selectors/GatingSelectors';
import { isHubSpotDotComAuthedUserRedirect } from '../utils/appUtils';
import { Metrics } from '../utils/metrics';
import { useQueryParamFromLocation } from '../utils/urls/appQueryParamUtilsLegacy';
import { useProductInventory } from '../v2/hooks/inventory/useProductInventory';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const withGlobalStylingOverrides = Component => {
  return props => {
    const theme = useTheme();
    const isTrellis = isTrellisTheme(theme);
    const ref = useRef(null);
    const {
      pathname
    } = useLocation();
    const isAppAuth = useAppSelector(isAppAuthSelector);
    const isInterstitial = pathname.includes('interstitial');
    useEffect(() => {
      const bgColor = !isInterstitial ? theme.color['fill-surface-recessed'] : theme.color['fill-surface-default'];
      document.body.style.backgroundColor = bgColor;
      return () => {
        document.body.style.backgroundColor = '';
      };
    }, [theme, isInterstitial]);
    useEffect(() => {
      var _ref$current;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.scrollIntoView();
    }, [pathname]);
    const savedQueryParams = getRedirectQueryParamsForCheckout();
    const isFromDotCom = Object.keys(savedQueryParams).some(param => ['hubs_content', 'hubs_signup-url'].includes(param));
    const useBranding = isTrellis && (isFromDotCom || !isAppAuth);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [useBranding && /*#__PURE__*/_jsx(GlobalWebBrandingStyles, {}), isTrellis && /*#__PURE__*/_jsx(SideNavOverrideStyles, {}), /*#__PURE__*/_jsx("div", {
        ref: ref,
        "aria-hidden": true
      }), /*#__PURE__*/_jsx(Component, Object.assign({}, props))]
    });
  };
};
const LoadableBaseLayout = makeLoadable({
  loader: () => import( /* webpackChunkName: "BaseLayout" */'pricing-pages-ui/layouts/BaseLayout')
});
const AppLayout = ({
  children,
  location
}) => {
  const isBillingAdmin = useSelector(isBillingAdminSelector);
  const isAppAuth = useSelector(isAppAuthSelector);
  const areGatesPopulated = useAppSelector(areGatesPopulatedSelector);
  const currencyCode = useDisplayedCurrencyCode();
  const upgradeSource = useQueryParamFromLocation(UPGRADE_SOURCE);
  const isRedirect = isHubSpotDotComAuthedUserRedirect();
  useEffect(() => {
    getPrimaryTracker().setProperties({
      isBillingAdmin,
      upgradeSource,
      inAppOrPublic: isAppAuth ? 'in-app' : 'public',
      activeCurrencyCode: currencyCode,
      isDotComAuthedRedirect: isAppAuth && isRedirect
    });
    if (isRedirect) {
      Metrics.counter('redirected-from-dotcom').increment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePricingPageCurrencies({
    isAppAuth
  });
  useBulkPurchaseOptions();
  useProductInventory();
  const {
    hasMismatchedProductsAndPopData,
    hasMismatchedSeatsGating,
    mismatchedSeatsGatingMetrics,
    mismatchedProductsAndPopDataMetrics,
    loading: isMismatchedCohortingCheckLoading
  } = useMismatchedCohortingCheck();
  if (hasMismatchedProductsAndPopData && !isMismatchedCohortingCheckLoading) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: MISMATCHED_RESPONSES,
      additionalErrorLogging: mismatchedProductsAndPopDataMetrics
    });
  }
  if (hasMismatchedSeatsGating && !isMismatchedCohortingCheckLoading) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: MISMATCHED_SEATS_GATING,
      additionalErrorLogging: mismatchedSeatsGatingMetrics
    });
  }
  if (isAppAuth) {
    return children;
  }

  /**
   * If we came from public hubspot.com/pricing render the dot com marketing nav.
   * This represents authenticated users who go to the website pricing pages
   * We want to preserve the dot com nav since otherwise it would lead to a weird
   * experience where they'd lose their nav and couldn't go back without using the
   * browser back button
   */

  if (areGatesPopulated) {
    return /*#__PURE__*/_jsx(LoadableBaseLayout, {
      isLoggedIn: isAppAuth,
      pathname: location.pathname,
      children: children
    });
  }
};
export default withGlobalStylingOverrides(withRouter(AppLayout));